import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, Navigate } from "react-router-dom";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { getServiceBySlug } from "@/data/services";
import { useLocalize } from "@/data/localize";
import { SEO } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2, Users, ClipboardList, ClipboardCheck } from "lucide-react";

export default function ServiceDetailPage() {
    const { t } = useTranslation();
    const { localize } = useLocalize();
    const { slug } = useParams();
    const raw = getServiceBySlug(slug);
    if (!raw) return <Navigate to="/services" replace />;
    const svc = localize(raw);

    const Icon = Icons[svc.icon] || Icons.Stethoscope;

    return (
        <>
            <SEO title={svc.name} description={svc.short} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ to: "/services", label: t("servicesPage.seoTitle") }, { label: svc.name }]} />
            </div>

            <section data-testid="service-detail" className="container-page pt-6 pb-8">
                <div className="grid gap-8 lg:grid-cols-12 items-start">
                    <div className="lg:col-span-8">
                        <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                            <Icon className="h-5.5 w-5.5" />
                        </div>
                        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                            {svc.name}
                        </h1>
                        <p className="mt-3 text-lg text-[color:var(--text-muted)] leading-relaxed max-w-2xl">
                            {svc.tagline}
                        </p>
                        <div className="mt-6">
                            <Button asChild size="lg" data-testid="service-detail-cta">
                                <Link to="/referrals">{t("cta.howReferralsWork")}</Link>
                            </Button>
                        </div>
                    </div>
                    <aside className="lg:col-span-4 card-soft p-6 bg-[color:var(--bg-subtle)]">
                        <p className="text-xs uppercase tracking-wider text-[color:var(--text-soft)]">{t("serviceDetail.atAGlance")}</p>
                        <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                            {svc.short}
                        </p>
                        {svc.prep_link && (
                            <Link
                                to={svc.prep_link}
                                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-teal-2)]"
                                data-testid="service-detail-prep-link"
                            >
                                {t("serviceDetail.prepGuide")}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        )}
                    </aside>
                </div>
            </section>

            <section className="container-page pb-8">
                <div className="grid gap-8 lg:grid-cols-12">
                    <article className="lg:col-span-8 prose-clinical">
                        <h2>{t("serviceDetail.overview")}</h2>
                        <p>{svc.overview}</p>

                        <h2 className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[color:var(--brand-teal-2)]" /> {t("serviceDetail.benefits")}</h2>
                        <ul>{(svc.benefits || []).map((b) => <li key={b}>{b}</li>)}</ul>

                        <h2 className="flex items-center gap-2"><Users className="h-5 w-5 text-[color:var(--brand-teal-2)]" /> {t("serviceDetail.idealFor")}</h2>
                        <ul>{(svc.ideal_for || []).map((i) => <li key={i}>{i}</li>)}</ul>

                        {svc.what_to_expect && svc.what_to_expect.length > 0 && (
                            <>
                                <h2 className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-[color:var(--brand-teal-2)]" /> {t("serviceDetail.whatToExpect")}</h2>
                                <ol>{svc.what_to_expect.map((w) => <li key={w}>{w}</li>)}</ol>
                            </>
                        )}
                    </article>

                    <aside className="lg:col-span-4">
                        <div className="card-soft p-6 sticky top-24">
                            <div className="flex items-center gap-2">
                                <ClipboardCheck className="h-5 w-5 text-[color:var(--brand-teal-2)]" />
                                <p className="font-display text-lg font-semibold text-[color:var(--brand-navy)]">{t("serviceDetail.nextStep")}</p>
                            </div>
                            <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                                {t("serviceDetail.questionsAbout", { name: (svc.name || "").toLowerCase() })}
                            </p>
                            <div className="mt-4 flex flex-col gap-2">
                                <Button asChild data-testid="service-detail-book-cta">
                                    <Link to="/referrals">{t("cta.howReferralsWork")}</Link>
                                </Button>
                                <Button asChild variant="outline">
                                    <Link to="/virtual-consultations">{t("serviceDetail.virtualConsultations")}</Link>
                                </Button>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </>
    );
}
